import { useEffect, useId } from "react";
import type { Berth } from "@services/reference-data/types";
import { getAnchorageAreas, getBerths, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { AnchorageArea } from "@services/vessel-call/types";
import { useQuery } from "@tanstack/react-query";
import {
  clearFieldNavigationTiming,
  startFieldNavigationTiming,
} from "@telemetry/fieldNavigationTiming";
import { useTrackPendingFieldNavigation } from "@telemetry/trackPendingFieldNavigation";

const VESSEL_CALL_SERVICE = "bff-vessel-call";

export const useBerthAnchorageAreaOptions = (
  fromToLocationCode?: string,
  locationArea?: string,
  fromField = "operationLocation",
) => {
  const timingId = useId();
  useEffect(() => () => clearFieldNavigationTiming(timingId), [timingId]);
  const berthQuery = useQuery<Berth[]>({
    queryKey: [REFERENCE_DATA_TYPES.BERTHS, fromToLocationCode],
    queryFn: () =>
      locationArea === "BERTH" ? getBerths(VESSEL_CALL_SERVICE, fromToLocationCode) : [],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const anchorageQuery = useQuery<AnchorageArea[]>({
    queryKey: [REFERENCE_DATA_TYPES.ANCHORAGE_AREAS, fromToLocationCode],
    queryFn: () =>
      locationArea === "ANCH" ? getAnchorageAreas(VESSEL_CALL_SERVICE, fromToLocationCode) : [],
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const { data: berths = [], isLoading: isBerthsLoading } = berthQuery;
  const { data: anchorageAreas = [], isLoading: isAnchorageAreasLoading } = anchorageQuery;
  useTrackPendingFieldNavigation(timingId, locationArea === "BERTH" ? berthQuery : anchorageQuery);

  return {
    startLocationNavigation: (nextLocation?: string, nextArea?: string) => {
      clearFieldNavigationTiming(timingId);
      if (
        !nextLocation ||
        nextLocation === fromToLocationCode ||
        !["BERTH", "ANCH"].includes(nextArea ?? "")
      )
        return;
      startFieldNavigationTiming(timingId, {
        module: "vessel_call",
        screen: "PortServiceRequest",
        action: "load_berth_anchorage_options",
        entityType: "port_service",
        fromField,
        toField: "berthOrAnchorageCode",
      });
    },
    options: [
      ...(berths?.map((berth) => ({ value: berth.berthCode, label: berth.berthName })) ?? []),
      ...(anchorageAreas?.map((anchorageArea) => ({
        value: anchorageArea.anchorageAreaCode,
        label: anchorageArea.anchorageAreaDesc,
      })) ?? []),
    ],
    isLoading: isBerthsLoading || isAnchorageAreasLoading,
  };
};
